import { Illustration, Table, Text, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React, { useMemo } from 'react';
import { DimensionValue, FlatList } from 'react-native';

import { CustomersListResponseNormalized } from '../../../../store/blockList/selector';
import { makeLoadingTable } from '../../../../utils/makeLoadingTable';

import { Head } from './components/Head';
import { RestrictedCustomersListItem } from './components/Item';
import { Loading } from './components/Loading';
import { rowHeight } from './constants';

export interface ItemData {
  sendId: string;
  monthCompetence: string;
  type: string;
  billedCustomers: number;
  sendDate: string;
  total: string;
  status: string;
}

interface SentOrderListProps {
  isMobile?: boolean;
  blockList?: CustomersListResponseNormalized[];
  blockListLoading?: boolean;
  blockListRetryLoading: boolean;
  onLoadMore?: () => void;
  filtered: boolean;
  sendRetryUnlockCustomer: ({
    customerId,
    channel,
    optoutId,
  }: {
    customerId: number;
    channel: string;
    optoutId;
  }) => void;
  currentOptoutId: string;
}

export const RestrictedCustomersList: React.FC<SentOrderListProps> = ({
  isMobile,
  blockList,
  blockListLoading,
  blockListRetryLoading,
  onLoadMore,
  filtered,
  sendRetryUnlockCustomer,
  currentOptoutId,
}) => {
  const loadingTable = useMemo(() => makeLoadingTable({ numRows: 1, numCols: 1 }), []);
  const loadingCardItems = Array.from({ length: 1 }, (_, index) => <Loading key={index} />);

  const deviceType = useBreakpointValue({ lg: 'desktop', sm: 'tablet' });

  const content = useMemo(() => {
    if (!blockList?.length && !filtered && !blockListLoading) {
      return (
        <VStack alignItems="center" paddingY={Spacing['stack.xxxl']}>
          <VStack alignItems="center" space={Spacing['stack.quarck']}>
            <Illustration name="NotFoundIllustration" testId={'empty-illustration'} />
            <Text
              textAlign={'center'}
              paddingTop={Spacing['stack.xxxs']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              variant="bold"
              color="neutral.black">
              Pesquise por clientes restritos.
            </Text>
            <Text
              textAlign={'center'}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              lineHeight="medium"
              color="neutral.black">
              Pesquise por nome, conta ou CPF/CNPJ para encontrar <br /> clientes restritos.
            </Text>
          </VStack>
        </VStack>
      );
    }

    return (
      <VStack flex={1}>
        {isMobile ? (
          <FlatList
            data={blockList}
            windowSize={10}
            maxToRenderPerBatch={4}
            contentContainerStyle={{ flex: 1 }}
            renderItem={({ item }) => (
              <RestrictedCustomersListItem
                currentOptoutId={currentOptoutId}
                blockListRetryLoading={blockListRetryLoading}
                item={item}
                sendRetryUnlockCustomer={sendRetryUnlockCustomer}
              />
            )}
            keyExtractor={(item) => item.optoutId!.toString()}
            showsVerticalScrollIndicator={false}
            onEndReached={onLoadMore}
            ListFooterComponent={blockListLoading ? <>{loadingCardItems}</> : null}
          />
        ) : (
          <Table
            isLoading={!blockList?.length && !!blockListLoading}
            rows={4}
            cols={7}
            rowHeight={rowHeight}
            flex={1}>
            {deviceType === 'desktop' && <Head />}
            <FlatList
              updateCellsBatchingPeriod={5}
              data={blockList}
              windowSize={10}
              maxToRenderPerBatch={4}
              style={{
                flex: 1,
                marginLeft: `-${Spacing['inline.xxs']}` as DimensionValue,
                marginRight: `-${Spacing['inline.xxs']}` as DimensionValue,
              }}
              renderItem={({ item }) => (
                <RestrictedCustomersListItem
                  currentOptoutId={currentOptoutId}
                  blockListRetryLoading={blockListRetryLoading}
                  sendRetryUnlockCustomer={sendRetryUnlockCustomer}
                  item={item}
                />
              )}
              keyExtractor={(item) => item.optoutId!.toString()}
              showsVerticalScrollIndicator={false}
              onEndReached={onLoadMore}
              ListFooterComponent={blockListLoading ? loadingTable : null}
            />
          </Table>
        )}
      </VStack>
    );
  }, [
    blockList,
    filtered,
    blockListLoading,
    isMobile,
    onLoadMore,
    loadingCardItems,
    deviceType,
    loadingTable,
    currentOptoutId,
    blockListRetryLoading,
    sendRetryUnlockCustomer,
  ]);

  return <Box flex={1}>{content}</Box>;
};
